import { MathUtils } from 'three';
import { EventHub } from '../event-hub';
import { ForgeViewer } from '../forge-viewer';
import { ForgeViewerEvents } from '../forge-viewer.enums';
import { EventHubListener } from '../forge-viewer.types';
import { normaliseAngle } from '../forge-viewer.utils';

export const SPHERE_RADIUS = 250;

export enum FirstPersonToolEvents {
    PAN_CHANGE = 'pan-change',
    ZOOM_CHANGE = 'zoom-change',
}

export class FirstPersonTool implements Autodesk.Viewing.ToolInterface {
    private name = 'first-person-tool';

    private viewer: Autodesk.Viewing.GuiViewer3D | null = null;
    private forgeViewer: ForgeViewer;

    private isDragging = false;

    private pointerDownMouseX: number = 0;
    private pointerDownMouseY: number = 0;
    private pointerDownYaw: number = 0;
    private pointerDownPitch: number = 0;

    private pitch: number = 0;
    private yaw: number = 0;

    private defaultPitch: number = 0;
    private defaultYaw: number = 0;

    private minFov = 30;
    private maxFov = 90;

    private eventHub: EventHub;

    // Pre-allocated vectors to reduce GC pressure
    private readonly _tempDir = new THREE.Vector3();
    private readonly _tempPosition = new THREE.Vector3();
    private readonly _tempTarget = new THREE.Vector3();

    // Animation state for cancellation
    private _animationFrameId: number | null = null;

    constructor(options: { defaultPitch?: number; defaultYaw?: number }) {
        const { defaultPitch = 0, defaultYaw = 0 } = options;
        this.defaultPitch = defaultPitch;
        this.defaultYaw = defaultYaw;
        this.eventHub = new EventHub();
    }

    private updateRotation(values: { pitch: number; yaw: number }) {
        const { pitch, yaw } = values;

        // Store values (clamping is handled by CameraController.setRotation)
        this.pitch = pitch;
        this.yaw = yaw;

        this.setRotation({ yaw: this.yaw, pitch: this.pitch });
    }

    private handleRotationChange(values: { pitch: number; yaw: number }) {
        if (!this.isDragging) {
            this.pointerDownYaw = values.yaw;
            this.pointerDownPitch = values.pitch;

            this.pitch = values.pitch;
            this.yaw = values.yaw;
        }
    }

    getForgeViewer(): ForgeViewer | null {
        return this.forgeViewer || null;
    }

    private convertFovToZoom(fov: number) {
        return Math.round(((this.maxFov - fov) / (this.maxFov - this.minFov)) * 100);
    }

    private convertZoomToFov(zoom: number) {
        return this.maxFov - (zoom / 100) * (this.maxFov - this.minFov);
    }

    // Event API
    on(event: ForgeViewerEvents | string, fn: EventHubListener<any>) {
        this.eventHub.on(event, fn);
    }

    off(event: ForgeViewerEvents | string, fn?: EventHubListener<any>) {
        this.eventHub.off(event, fn);
    }

    getFOV(): number {
        const camera = this.viewer.getCamera() as THREE.PerspectiveCamera;

        return camera.fov;
    }

    setFOV(fov: number) {
        // this.viewer.navigation.setVerticalFov(fov, true);
        // this.viewer.setFOV(fov);

        const camera = this.viewer.getCamera() as THREE.PerspectiveCamera;

        camera.fov = fov;

        camera.updateProjectionMatrix();

        this.viewer.impl.sceneUpdated(true);

        this.eventHub.emit(FirstPersonToolEvents.ZOOM_CHANGE, this.convertFovToZoom(camera.fov));
        // this._onFOVChange?.({ fov });
    }

    getZoom(): number {
        const camera = this.viewer.getCamera() as THREE.PerspectiveCamera;

        return this.convertFovToZoom(camera.fov);
    }

    setZoom(zoom: number) {
        const fov = this.convertZoomToFov(zoom);

        this.setFOV(fov);
    }

    animateToPosition(position: THREE.Vector3, duration = 1000) {
        // Cancel any existing animation
        if (this._animationFrameId !== null) {
            cancelAnimationFrame(this._animationFrameId);
            this._animationFrameId = null;
        }

        const nav = this.viewer.navigation;
        const camera = this.viewer.getCamera();

        const startPos = camera.position.clone();
        const originalTarget = nav.getTarget().clone();
        const direction = originalTarget.clone().sub(startPos).normalize();

        const targetPos = position.clone();
        const targetLook = targetPos.clone().add(direction);

        const up = camera.up.clone();

        // Pre-allocate vectors for animation loop
        const currentPos = new THREE.Vector3();
        const currentTarget = new THREE.Vector3();

        const startTime = performance.now();

        const animate = (time: number) => {
            const t = Math.min((time - startTime) / duration, 1);

            // Smoothstep easing (nicer than linear)
            const s = t * t * (3 - 2 * t);

            // Reuse vectors instead of cloning each frame
            currentPos.copy(startPos).lerp(targetPos, s);
            currentTarget.copy(originalTarget).lerp(targetLook, s);

            nav.setView(currentPos, currentTarget);
            camera.up.copy(up);

            this.viewer.impl.sceneUpdated(true);

            if (t < 1) {
                this._animationFrameId = requestAnimationFrame(animate);
            } else {
                this._animationFrameId = null;
            }
        };

        this._animationFrameId = requestAnimationFrame(animate);
    }

    getRotation() {
        const nav = this.viewer.navigation;

        // Reuse pre-allocated vectors
        this._tempPosition.copy(nav.getPosition());
        this._tempTarget.copy(nav.getTarget());

        const dir = this._tempDir.copy(this._tempTarget).sub(this._tempPosition).normalize();

        // dir.x = sin(yaw) * cos(pitch)
        // dir.y = cos(yaw) * cos(pitch)
        // dir.z = sin(pitch)

        // ---- PITCH ----
        const pitchRad = Math.asin(dir.z);
        const pitch = MathUtils.radToDeg(pitchRad);

        // ---- YAW ----
        // atan2(x, y) since you defined:
        // x = sin(yaw)*cos(pitch)
        // y = cos(yaw)*cos(pitch)
        const yawRad = Math.atan2(dir.x, dir.y);
        let yaw = MathUtils.radToDeg(yawRad);

        // normalize 0–360
        yaw = MathUtils.euclideanModulo(yaw, 360);

        // Return rotation relative to default values
        return {
            yaw: normaliseAngle(yaw) - this.defaultYaw,
            pitch: pitch - this.defaultPitch,
        };
    }

    setRotation(values: { yaw: number; pitch: number }) {
        let { yaw, pitch } = values;

        // Apply rotation relative to default values
        pitch = this.defaultPitch + pitch;
        yaw = this.defaultYaw + yaw;

        pitch = Math.max(-85, Math.min(85, pitch));
        yaw = normaliseAngle(yaw); // stays 0–360

        const nav = this.viewer.navigation;

        // Reuse pre-allocated vectors
        this._tempPosition.copy(nav.getPosition());
        this._tempTarget.copy(nav.getTarget());

        const radius = this._tempPosition.distanceTo(this._tempTarget);

        const yawRad = MathUtils.degToRad(yaw);
        const pitchRad = MathUtils.degToRad(pitch);

        this._tempDir
            .set(
                Math.sin(yawRad) * Math.cos(pitchRad), // X
                Math.cos(yawRad) * Math.cos(pitchRad), // Y
                Math.sin(pitchRad) // Z
            )
            .normalize()
            .multiplyScalar(radius);

        this._tempTarget.copy(this._tempPosition).add(this._tempDir);

        nav.setView(this._tempPosition, this._tempTarget);

        this.viewer.impl.sceneUpdated(true);

        // Emit relative values (without defaults) so listeners get consistent values
        this.eventHub.emit(FirstPersonToolEvents.PAN_CHANGE, {
            pitch: pitch - this.defaultPitch,
            yaw: normaliseAngle(yaw) - this.defaultYaw,
        });
    }

    getPan() {
        return this.getRotation();
    }

    setPan(values: { yaw: number; pitch: number }) {
        this.setRotation(values);
    }

    attach(forgeViewer: ForgeViewer) {
        this.forgeViewer = forgeViewer;

        forgeViewer.instance.toolController.registerTool(this);
        forgeViewer.instance.toolController.activateTool(this.name);
    }

    getName(): string {
        return this.name;
    }

    getNames(): string[] {
        return [this.name];
    }

    getPriority?(): number {
        return 50;
    }

    register(): void {
        this.on(FirstPersonToolEvents.PAN_CHANGE, this.handleRotationChange.bind(this));
    }

    deregister(): void {
        this.off(FirstPersonToolEvents.PAN_CHANGE, this.handleRotationChange.bind(this));
    }

    activate(name: string, viewer?: Autodesk.Viewing.GuiViewer3D): void {
        this.viewer = viewer;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    deactivate(name: string): void {
        // Cancel any pending animation on deactivation
        if (this._animationFrameId !== null) {
            cancelAnimationFrame(this._animationFrameId);
            this._animationFrameId = null;
        }
    }

    update(highResTimestamp?: number): boolean {
        return false;
    }

    // handleSingleClick?(event: MouseEvent, button: number): boolean {
    //     if (button !== 0) return false;

    //     return true;
    // }

    handleDoubleClick?(event: MouseEvent, button: number): boolean {
        if (button !== 0) return false;

        return true;
    }

    // handleSingleTap?(): boolean {
    //     return true;
    // }

    handleDoubleTap?(): boolean {
        return true;
    }

    // handleKeyDown?(event: KeyboardEvent, keyCode: number): boolean {
    //     return false;
    // }

    // handleKeyUp?(event: KeyboardEvent, keyCode: number): boolean {
    //     return false;
    // }

    handleWheelInput?(delta: number): boolean {
        if (!this.viewer) return;

        const camera = this.viewer.getCamera() as THREE.PerspectiveCamera;

        const fov = MathUtils.clamp(camera.fov - delta, this.minFov, this.maxFov);

        // camera.fov = MathUtils.clamp(fov, this.minFov, this.maxFov);
        // camera.updateProjectionMatrix();

        // this.viewer.impl.sceneUpdated(true);
        this.setFOV(fov);

        return true;
    }

    handleButtonDown?(event: MouseEvent, button: number): boolean {
        if (button !== 0) return false;

        this.pointerDownMouseX = event.clientX;
        this.pointerDownMouseY = event.clientY;
        this.pointerDownYaw = this.yaw;
        this.pointerDownPitch = this.pitch;

        this.isDragging = true;

        return true;
    }

    handleButtonUp?(event: MouseEvent, button: number): boolean {
        if (button !== 0) return false;

        this.isDragging = false;

        this.updateRotation({ pitch: this.pitch, yaw: this.yaw });

        return true;
    }

    handleMouseMove?(event: MouseEvent): boolean {
        if (!this.isDragging) {
            return true;
        }

        this.yaw = (this.pointerDownMouseX - event.clientX) * 0.1 + this.pointerDownYaw;
        this.pitch = (event.clientY - this.pointerDownMouseY) * 0.1 + this.pointerDownPitch;

        this.updateRotation({ pitch: this.pitch, yaw: this.yaw });

        return true;
    }

    // handleGesture?(event: Event): boolean {}

    // handleBlur?(event: Event): boolean {}

    // handleResize?(): void {}
}
